import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CustomChemicalService } from '../../../../../core/services/custom-chemical.service';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

@Component({
  selector: 'app-custom-chemical-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, MaxLengthDirective, CharCounterDirective],
  templateUrl: './custom-chemical-form.component.html',
  styleUrls: ['./custom-chemical-form.component.scss']
})
export class CustomChemicalFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isEditMode = false;
  chemicalId: string | null = null;
  isLoading = false;
  isSaving = false;
  private cropBlurTimeout: ReturnType<typeof setTimeout> | null = null;

  private customChemicalService = inject(CustomChemicalService);

  // Chemical categories (matching admin)
  chemicalCategories = [
    { value: 'herbicide', label: 'Herbicide' },
    { value: 'insecticide', label: 'Insecticide' },
    { value: 'fungicide', label: 'Fungicide' },
    { value: 'fertilizer', label: 'Fertilizer' },
    { value: 'adjuvant', label: 'Adjuvant' },
    { value: 'igr', label: 'IGR (Insect Growth Regulator)' },
    { value: 'miticide', label: 'Miticide' },
    { value: 'pgr', label: 'PGR (Plant Growth Regulator)' },
    { value: 'other', label: 'Other' }
  ];

  // Signal words
  signalWords = [
    { value: 'CAUTION', label: 'CAUTION - Slightly toxic' },
    { value: 'WARNING', label: 'WARNING - Moderately toxic' },
    { value: 'DANGER', label: 'DANGER - Highly toxic' }
  ];

  // Common crop suggestions
  cropSuggestions = [
    'Corn', 'Soybeans', 'Wheat', 'Cotton', 'Alfalfa', 'Canola',
    'Sunflowers', 'Rice', 'Sorghum', 'Barley', 'Oats',
    'Apple Trees', 'Pear Trees', 'Citrus', 'Grapes', 'Vegetables', 'All crops'
  ];

  // Tag-input state
  targetPestsTags: string[] = [];
  cropsTags: string[] = [];
  newPestInput = '';
  newCropInput = '';
  showCropSuggestions = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.checkEditMode();
  }

  ngOnDestroy(): void {
    if (this.cropBlurTimeout) {
      clearTimeout(this.cropBlurTimeout);
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      epaNumber: [''],
      manufacturer: [''],
      activeIngredient: [''],
      signalWord: ['CAUTION'],
      category: ['fertilizer', Validators.required],
      formulation: [''],
      rei: [''],
      phi: [''],
      defaultRate: ['', Validators.required],
      notes: ['']
    });
  }

  // --- Pest tag management ---

  addPestTag(): void {
    const value = this.newPestInput.trim();
    if (value && !this.targetPestsTags.includes(value)) {
      this.targetPestsTags.push(value);
      this.newPestInput = '';
    }
  }

  removePestTag(index: number): void {
    this.targetPestsTags.splice(index, 1);
  }

  onPestInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addPestTag();
    }
  }

  clearAllPests(): void {
    this.targetPestsTags = [];
  }

  // --- Crop tag management ---

  addCropTag(): void {
    const value = this.newCropInput.trim();
    if (value && !this.cropsTags.includes(value)) {
      this.cropsTags.push(value);
      this.newCropInput = '';
      this.showCropSuggestions = false;
    }
  }

  removeCropTag(index: number): void {
    this.cropsTags.splice(index, 1);
  }

  onCropInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addCropTag();
    }
  }

  clearAllCrops(): void {
    this.cropsTags = [];
  }

  addCropFromSuggestion(crop: string): void {
    if (!this.cropsTags.includes(crop)) {
      this.cropsTags.push(crop);
    }
    this.newCropInput = '';
    this.showCropSuggestions = false;
  }

  onCropInputFocus(): void {
    if (this.cropBlurTimeout) {
      clearTimeout(this.cropBlurTimeout);
      this.cropBlurTimeout = null;
    }
    this.showCropSuggestions = true;
  }

  onCropInputBlur(): void {
    // Clear any pending timeout before scheduling a new one
    if (this.cropBlurTimeout) {
      clearTimeout(this.cropBlurTimeout);
    }
    // Delay to allow click on suggestion to register
    this.cropBlurTimeout = setTimeout(() => {
      this.showCropSuggestions = false;
      this.cropBlurTimeout = null;
    }, 200);
  }

  get filteredCropSuggestions(): string[] {
    const query = this.newCropInput.trim().toLowerCase();
    return this.cropSuggestions.filter(
      crop => !this.cropsTags.includes(crop) && (!query || crop.toLowerCase().includes(query))
    );
  }

  // --- Edit mode ---

  private checkEditMode(): void {
    this.chemicalId = this.route.snapshot.paramMap.get('id');
    if (this.chemicalId) {
      this.isEditMode = true;
      this.loadChemical(this.chemicalId);
    }
  }

  private async loadChemical(id: string): Promise<void> {
    this.isLoading = true;

    this.targetPestsTags = [];
    this.cropsTags = [];

    try {
      const chemical = await this.customChemicalService.getChemicalById(id);
      if (chemical) {
        this.form.patchValue({
          name: chemical.name,
          epaNumber: chemical.epaNumber || '',
          manufacturer: chemical.manufacturer || '',
          activeIngredient: chemical.activeIngredient || '',
          signalWord: chemical.signalWord || 'CAUTION',
          category: chemical.category,
          formulation: chemical.formulation || '',
          rei: chemical.rei || '',
          phi: chemical.phi || '',
          defaultRate: chemical.defaultRate || '',
          notes: chemical.notes || ''
        });

        if (chemical.targetPests) {
          this.targetPestsTags = [...chemical.targetPests];
        }

        if (chemical.crops) {
          this.cropsTags = [...chemical.crops];
        }
      }
    } catch (error) {
      console.error('Error loading chemical:', error);
    } finally {
      this.isLoading = false;
    }
  }

  getCategoryDescription(): string {
    const category = this.form.get('category')?.value;
    switch (category) {
      case 'herbicide':
        return 'Controls unwanted weeds and vegetation';
      case 'insecticide':
        return 'Controls insect pests';
      case 'fungicide':
        return 'Controls fungal diseases';
      case 'fertilizer':
        return 'Provides essential nutrients to crops';
      case 'adjuvant':
        return 'Enhances the performance of other chemicals';
      case 'igr':
        return 'Disrupts insect growth and development';
      case 'miticide':
        return 'Controls mites and ticks';
      case 'pgr':
        return 'Regulates plant growth and development';
      case 'other':
        return 'Other agricultural products';
      default:
        return '';
    }
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    if (this.cropsTags.length === 0) {
      return;
    }

    this.isSaving = true;

    try {
      const chemicalData = {
        name: this.form.value.name,
        epaNumber: this.form.value.epaNumber || undefined,
        manufacturer: this.form.value.manufacturer || undefined,
        activeIngredient: this.form.value.activeIngredient || undefined,
        signalWord: this.form.value.signalWord || undefined,
        category: this.form.value.category,
        formulation: this.form.value.formulation || undefined,
        rei: this.form.value.rei || undefined,
        phi: this.form.value.phi || undefined,
        defaultRate: this.form.value.defaultRate || undefined,
        targetPests: this.targetPestsTags.length > 0 ? this.targetPestsTags : undefined,
        crops: this.cropsTags,
        notes: this.form.value.notes || undefined
      };

      let result;
      if (this.isEditMode && this.chemicalId) {
        result = await this.customChemicalService.updateChemical(this.chemicalId, chemicalData);
      } else {
        result = await this.customChemicalService.createChemical(chemicalData);
      }

      if (result) {
        this.router.navigate(['/logbook/chemicals/custom']);
      } else {
        console.error('Failed to save chemical');
      }
    } catch (error) {
      console.error('Error saving chemical:', error);
    } finally {
      this.isSaving = false;
    }
  }

  onCancel(): void {
    this.router.navigate(['/logbook/chemicals/custom']);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }
}
